(() => {
  const journeys = document.querySelectorAll('[data-bw-journey]');
  if (!journeys.length || !window.matchMedia('(max-width: 989px)').matches) return;

  journeys.forEach((journey) => {
    const scenes = Array.from(journey.querySelectorAll('[data-bw-scene]'));
    const dots = Array.from(journey.querySelectorAll('[data-bw-progress]'));

    scenes.forEach((scene) => {
      const cta = scene.querySelector('.bw-scene__link[href]');
      if (!cta) return;
      const title = scene.querySelector('.bw-scene__title-row h2')?.textContent.trim();
      if (title) cta.setAttribute('aria-label', `View ${title}`);
    });

    /* Adjacent scenes visually overlap on mobile. Correct only the tap target. */
    let forwardingCtaClick = false;
    const protectCtaHitAreas = (event) => {
      if (forwardingCtaClick || event.defaultPrevented) return;
      if (typeof event.button === 'number' && event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const x = event.clientX;
      const y = event.clientY;
      if (!Number.isFinite(x) || !Number.isFinite(y)) return;

      const matchingCta = Array.from(journey.querySelectorAll('.bw-scene__link[href]')).find((cta) => {
        const rect = cta.getBoundingClientRect();
        return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
      });

      if (!matchingCta) return;
      const nativeCta = event.target instanceof Element ? event.target.closest('.bw-scene__link[href]') : null;
      if (nativeCta === matchingCta) return;

      event.preventDefault();
      event.stopPropagation();
      forwardingCtaClick = true;
      try { matchingCta.click(); } finally { forwardingCtaClick = false; }
    };

    journey.addEventListener('click', protectCtaHitAreas, true);

    const setActive = (index) => {
      dots.forEach((dot, dotIndex) => {
        const active = dotIndex === index;
        dot.classList.toggle('is-active', active);
        if (active) dot.setAttribute('aria-current', 'step');
        else dot.removeAttribute('aria-current');
      });
    };

    const warmScene = (scene, priority = 'auto') => {
      if (!scene || scene.dataset.bwWarmed === 'true') return;
      const image = scene.querySelector('.bw-scene__image');
      if (image) {
        image.loading = 'eager';
        try { image.fetchPriority = priority; } catch (error) {}
        if (priority === 'high' && typeof image.decode === 'function') image.decode().catch(() => {});
      }
      scene.classList.add('is-visible');
      scene.dataset.bwWarmed = 'true';
    };

    warmScene(scenes[0], 'high');

    if ('IntersectionObserver' in window) {
      const preloadObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const index = Number(entry.target.dataset.bwIndex);
          warmScene(entry.target, 'auto');
          warmScene(scenes[index + 1], 'auto');
          preloadObserver.unobserve(entry.target);
        });
      }, { rootMargin: '55% 0px 55% 0px', threshold: 0.01 });

      const activeObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const index = Number(entry.target.dataset.bwIndex);
          setActive(index);
          warmScene(scenes[index + 1], 'auto');
        });
      }, { threshold: 0.48 });

      scenes.forEach((scene) => {
        preloadObserver.observe(scene);
        activeObserver.observe(scene);
      });
    } else {
      scenes.forEach((scene) => warmScene(scene, 'auto'));
    }

    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        warmScene(scenes[index], 'auto');
        scenes[index]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  });
})();

/* PickALeaf Discover by World exact route lock v183.
 * Never derive a product from collection position. Each visual scene owns one exact
 * product handle and one category title on both mobile and desktop.
 */
(() => {
  'use strict';

  const WORLD_ROUTES = {
    'blossom-wood': [
      ['brown-embroidered-handbag', 'Satchel Bag'],
      ['brown-embroidered-crossbody-bag', 'Crossbody Bag'],
      ['brown-large-work-tote-bag', 'Work Tote Bag'],
      ['brown-travel-luggage-duffel-bag', 'Duffel Bag'],
      ['brown-laptop-sleeve-bag', 'Laptop Sleeve'],
      ['brown-embroidered-tote-bag', 'Tote Bag']
    ],
    'daisy-dream': [
      ['purple-embroidered-handbag', 'Satchel Bag'],
      ['purple-ethinc-tote-bag', 'Tote Bag'],
      ['purple-work-tote-bag', 'Work Tote Bag'],
      ['purple-embroidered-travel-duffel-bag', 'Duffel Bag'],
      ['purple-slim-laptop-sleeve-bag', 'Laptop Sleeve']
    ],
    'garden-grey': [
      ['grey-designer-handbag', 'Satchel Bag'],
      ['grey-embroidered-tote-bag', 'Work Tote Bag'],
      ['grey-laptop-tote-bag', 'Tote Bag'],
      ['grey-embroidered-overnight-duffel-bag', 'Duffel Bag'],
      ['grey-laptop-sleeve-bag', 'Laptop Sleeve']
    ],
    'golden-flora': [
      ['mustard-unique-handbag', 'Satchel Bag'],
      ['mustard-embroidered-large-tote-bag', 'Work Tote Bag'],
      ['mustard-lightweight-tote-bag', 'Tote Bag'],
      ['mustard-embroidered-gym-duffel-bag', 'Duffel Bag'],
      ['mustard-laptop-sleeve-bag', 'Laptop Sleeve']
    ],
    'mystic-garden': [
      ['beige-designer-handbag', 'Satchel Bag'],
      ['beige-party-tote-bag', 'Work Tote Bag'],
      ['beige-large-tote-bag', 'Tote Bag'],
      ['beige-embroidered-weekender-duffel-bag', 'Duffel Bag'],
      ['beige-laptop-sleeve-bag', 'Laptop Sleeve'],
      ['beige-designer-crossbody-bag', 'Crossbody Bag']
    ],
    'olive-waves': [
      ['olive-green-party-handbag', 'Satchel Bag'],
      ['olive-green-daily-tote-bag', 'Work Tote Bag'],
      ['olive-green-embroidered-tote-bag', 'Tote Bag'],
      ['olive-green-gym-duffel-bag', 'Duffel Bag'],
      ['olive-green-laptop-sleeve-bag', 'Laptop Sleeve']
    ],
    'petal-eden': [
      ['pink-embroidered-wedding-handbag', 'Satchel Bag'],
      ['pink-wedding-tote-bag', 'Work Tote Bag'],
      ['pink-vacation-tote-bag', 'Tote Bag'],
      ['pink-weekender-travel-duffel-bag', 'Duffel Bag'],
      ['pink-work-laptop-sleeve-bag', 'Laptop Sleeve']
    ],
    'pink-paradise': [
      ['pink-bridal-tote-bag', 'Work Tote Bag'],
      ['pink-laptop-sleeve-bag', 'Laptop Sleeve'],
      ['pink-weekender-cabin-duffel-bag', 'Duffel Bag'],
      ['pink-floral-tote-bag', 'Tote Bag'],
      ['pink-embroidered-bridal-handbag', 'Satchel Bag']
    ],
    'violet-valley': [
      ['purple-embroidered-tote-bag', 'Tote Bag'],
      ['purple-embroidered-work-tote-bag', 'Work Tote Bag'],
      ['purple-airport-travel-duffel-bag', 'Duffel Bag'],
      ['purple-top-handle-bag', 'Satchel Bag'],
      ['purple-laptop-sleeve-bag', 'Laptop Sleeve']
    ]
  };

  const getWorld = () => {
    const match = window.location.pathname.match(/\/collections\/([^/?#]+)/i);
    return match ? decodeURIComponent(match[1]).toLowerCase() : '';
  };

  const exactRoutes = () => WORLD_ROUTES[getWorld()] || null;
  const productUrl = (handle) => `/products/${encodeURIComponent(handle)}`;

  const applyExactRoutes = () => {
    const routes = exactRoutes();
    if (!routes) return false;
    const journey = document.querySelector('.bw-journey[data-bw-journey]');
    if (!journey) return false;
    const scenes = Array.from(journey.querySelectorAll('[data-bw-scene]'));

    routes.forEach(([handle, title], index) => {
      const scene = scenes[index];
      if (!scene) return;
      const url = productUrl(handle);
      scene.dataset.pfExactProductHandle = handle;
      scene.dataset.pfExactWorldRoute = 'v183';

      const heading = scene.querySelector('.bw-scene__title-row h2');
      if (heading) heading.textContent = title;

      const link = scene.querySelector('.bw-scene__link');
      if (link) {
        link.href = url;
        link.dataset.pfExactProduct = 'v183';
        link.setAttribute('aria-label', `View ${title}`);
      }

      const image = scene.querySelector('.bw-scene__image');
      if (image) {
        image.dataset.pfExactProductHandle = handle;
        image.alt = image.alt.replace(/^(Satchel Bag|Crossbody Bag|Work Tote Bag|Tote Bag|Duffel Bag|Duffel Bag|Laptop Sleeve|Wedding Satchel Bag|Wedding Tote Bag|Vacation Tote Bag|Bridal Tote Bag|Floral Tote Bag|Embroidered Tote Bag|Duffel Bag|Top Handle Bag)/i, title);
      }
    });
    return true;
  };

  /* Window capture runs before the older document-level route helper. This guarantees
     the exact handle wins even when Liquid all_products lookup or collection fallback fails. */
  window.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const link = target && target.closest('.bw-journey[data-bw-journey] .bw-scene__link');
    if (!link) return;
    const routes = exactRoutes();
    if (!routes) return;
    const scene = link.closest('[data-bw-scene]');
    const journey = scene && scene.closest('.bw-journey[data-bw-journey]');
    if (!scene || !journey) return;
    const scenes = Array.from(journey.querySelectorAll('[data-bw-scene]'));
    const index = scenes.indexOf(scene);
    const route = routes[index];
    if (!route) return;

    event.preventDefault();
    event.stopPropagation();
    window.location.assign(productUrl(route[0]));
  }, true);

  if (!applyExactRoutes()) {
    const observer = new MutationObserver(() => {
      if (applyExactRoutes()) observer.disconnect();
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
    window.setTimeout(() => observer.disconnect(), 10000);
  }
  document.addEventListener('shopify:section:load', () => window.setTimeout(applyExactRoutes, 0));
})();
